import React from "react"

type CustomPaginationProps = {
  page: number
  numPages: number
  onPageChange: (page: number) => void
  maxVisiblePages?: number
  className?: string
}

function getVisiblePages(page: number, numPages: number, maxVisiblePages: number): (number | "ellipsis")[] {
  if (numPages <= maxVisiblePages) {
    return Array.from({ length: numPages }, (_, i) => i + 1)
  }

  const pages: (number | "ellipsis")[] = []
  const side = Math.max(1, Math.floor((maxVisiblePages - 3) / 2))

  let start = Math.max(2, page - side)
  let end = Math.min(numPages - 1, page + side)

  const visibleCount = end - start + 1
  const targetCount = maxVisiblePages - 2

  if (visibleCount < targetCount) {
    const missing = targetCount - visibleCount
    start = Math.max(2, start - missing)
    end = Math.min(numPages - 1, end + missing)
  }

  pages.push(1)

  if (start > 2) {
    pages.push("ellipsis")
  }

  for (let p = start; p <= end; p++) {
    pages.push(p)
  }

  if (end < numPages - 1) {
    pages.push("ellipsis")
  }

  pages.push(numPages)

  return pages
}

export function CustomPagination({
  page,
  numPages,
  onPageChange,
  maxVisiblePages = 7,
  className,
}: CustomPaginationProps) {
  if (!numPages || numPages <= 1) return null

  const safePage = Math.min(Math.max(page, 1), numPages)
  const visiblePages = getVisiblePages(safePage, numPages, maxVisiblePages)

  const goTo = (nextPage: number) => {
    if (nextPage < 1 || nextPage > numPages || nextPage === safePage) return
    onPageChange(nextPage)
  }

  const baseButtonStyle: React.CSSProperties = {
    minWidth: 36,
    height: 36,
    padding: "0 10px",
    border: "1px solid #d0d5dd",
    background: "white",
    borderRadius: 8,
    cursor: "pointer",
  }

  const activeButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    fontWeight: 700,
    border: "1px solid #0d6efd",
  }

  const disabledButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  }

  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <button
        type="button"
        onClick={() => goTo(safePage - 1)}
        disabled={safePage === 1}
        style={safePage === 1 ? disabledButtonStyle : baseButtonStyle}
      >
        ‹
      </button>

      {visiblePages.map((item, idx) =>
        item === "ellipsis" ? (
          <span key={`ellipsis-${idx}`} style={{ padding: "0 4px" }}>
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => goTo(item)}
            aria-current={item === safePage ? "page" : undefined}
            style={item === safePage ? activeButtonStyle : baseButtonStyle}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => goTo(safePage + 1)}
        disabled={safePage === numPages}
        style={safePage === numPages ? disabledButtonStyle : baseButtonStyle}
      >
        ›
      </button>
    </div>
  )
}